<script setup lang="ts">
/**
 * 魔導士の名のり ページ (#417) の bio 部分をレンダリングする共通コンポーネント。
 *
 * 閲覧ページ (/c/<slug>) と編集ダイアログのプレビュー領域の両方で使う。
 * Pyxel ファン向けの中立なデザイン (Noto Sans JP 本文 + Mincho 見出し +
 * 金色アクセント) を採用しており、編集ダイアログ側の fantasy 体裁とは
 * 意図的に異なる見た目になっている。
 */
interface Props {
  markdown: string
}
defineProps<Props>()
</script>

<template>
  <div class="creator-bio">
    <MDC :value="markdown" />
  </div>
</template>

<style scoped>
.creator-bio {
  font-family: "Noto Sans JP", sans-serif;
  font-size: 0.95rem;
  /* 親 (LargeDialog の .fantasy-dialog-text) が font-weight: bold を
     かけてくるケースがあるので明示的に normal に戻す。 */
  font-weight: normal;
  line-height: 1.8;
  color: #2a1f10;
}

.creator-bio :deep(p),
.creator-bio :deep(li),
.creator-bio :deep(blockquote),
.creator-bio :deep(code) {
  font-weight: normal;
}

/* <strong>/<b> は Markdown 通り太字を維持。 */
.creator-bio :deep(strong),
.creator-bio :deep(b) {
  font-weight: 700;
}

.creator-bio :deep(h1),
.creator-bio :deep(h2),
.creator-bio :deep(h3) {
  font-family: "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", "Noto Serif", serif;
  font-weight: 700;
  color: #1f3554;
  border-bottom: 1px solid rgba(140, 100, 50, 0.3);
  padding-bottom: 0.2em;
  margin-top: 1.2em;
  margin-bottom: 0.5em;
}

.creator-bio :deep(a) {
  color: #1f3554;
  text-decoration: underline;
  text-decoration-color: rgba(31, 53, 84, 0.4);
}

.creator-bio :deep(a:hover) {
  text-decoration-color: #1f3554;
}

.creator-bio :deep(img) {
  max-width: 100%;
  height: auto;
  border: 1px solid rgba(140, 100, 50, 0.35);
  border-radius: 2px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  margin: 0.6em 0;
}

.creator-bio :deep(blockquote) {
  border-left: 3px solid #c9a440;
  padding-left: 0.9em;
  color: #5a3c1a;
  font-style: italic;
  margin: 0.6em 0;
}

.creator-bio :deep(code) {
  background: rgba(140, 100, 50, 0.12);
  padding: 0.1em 0.4em;
  border-radius: 2px;
  font-family: "IBM Plex Mono", "Google Sans Code", monospace;
  font-size: 0.9em;
}

.creator-bio :deep(pre) {
  background: rgba(31, 44, 68, 0.95);
  color: #f4e4bf;
  padding: 0.9em 1em;
  border-radius: 4px;
  overflow-x: auto;
  margin: 0.6em 0;
}

.creator-bio :deep(ul),
.creator-bio :deep(ol) {
  margin: 0.4em 0;
  padding-left: 1.6em;
}
</style>
